
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type AIAssistantSettings = {
  enabled: boolean;
  a2aEnabled: boolean;
  multiAgentEnabled: boolean;
};

export const useAIAssistantSettings = () => {
  // Update default settings - both A2A and multiAgent enabled by default
  const [settings, setSettings] = useState<AIAssistantSettings>({
    enabled: true,
    a2aEnabled: true,
    multiAgentEnabled: true
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();
      
      if (!profile?.organization_id) {
        setIsLoading(false);
        return;
      }

      const { data: organization } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', profile.organization_id)
        .single();

      if (organization?.settings && typeof organization.settings === 'object') {
        const orgSettings = organization.settings as Record<string, unknown>;
        const aiSettings = orgSettings.aiAssistant as Partial<AIAssistantSettings> | undefined;
        
        if (aiSettings) {
          setSettings({
            enabled: aiSettings.enabled ?? true,
            a2aEnabled: aiSettings.a2aEnabled ?? true,
            multiAgentEnabled: aiSettings.multiAgentEnabled ?? true,
          });
        }
      }
    } catch (error) {
      console.error('Error loading AI settings:', error);
      toast.error('Failed to load AI assistant settings');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async (newSettings: AIAssistantSettings) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();
      
      if (!profile?.organization_id) throw new Error('No organization found');

      const { data: organization } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', profile.organization_id)
        .single();

      const currentSettings = organization?.settings && typeof organization.settings === 'object' 
        ? organization.settings as Record<string, unknown>
        : {};

      const updatedSettings = {
        ...currentSettings,
        aiAssistant: newSettings
      };

      const { error } = await supabase
        .from('organizations')
        .update({ settings: updatedSettings })
        .eq('id', profile.organization_id);

      if (error) throw error;
      setSettings(newSettings);
      toast.success('AI assistant settings saved');
    } catch (error) {
      console.error('Error saving AI settings:', error);
      toast.error('Failed to save AI assistant settings');
      throw error;
    }
  };

  return {
    settings,
    isLoading,
    saveSettings
  };
};
